import { FC } from 'react';
import { RadioGroup, Radio } from '@ordr-as/umami-react/radio';
import useIntl from '../../../../hooks/useIntl';
import { useLocation } from '../../../../context/LocationContext';
import { useQueryUnsettledDigitalBills } from '../../../../hooks/useDigitalBillApi';
import { useCheckout } from '../../../../context/CheckoutContext';
import UsersmultipleIcon from '@ordr-as/icons/UsersmultipleIcon';
import { useActiveDigitalBill, useDigitalBillActions } from '../../../../context/DigitalBillStore';

interface Props {
  onClose: () => void;
}

const SelectDigitalBill: FC<Props> = ({ onClose }) => {
  const intl = useIntl();
  const { location, counterDeliveryMethod, formatCurrency } = useLocation();
  const { data: digitalBills = [] } = useQueryUnsettledDigitalBills(location.locationId);
  const activeDigitalBill = useActiveDigitalBill();
  const { setActiveDigitalBill } = useDigitalBillActions();
  const { resetTableNumber, setToDefaultDeliveryMethod, tableNumber } = useCheckout();

  const handleChange = (reference: string) => {
    if (reference === 'None') {
      handleUnSelectDigitalBill();
      return;
    }

    const selectedDigitalBill = digitalBills.find((digitalBill) => digitalBill.reference === reference) || undefined;
    setActiveDigitalBill(selectedDigitalBill);

    resetTableNumber();

    onClose();
  };

  const handleUnSelectDigitalBill = () => {
    setActiveDigitalBill(undefined);

    resetTableNumber();
    if (!counterDeliveryMethod) {
      return;
    }
    setToDefaultDeliveryMethod();

    onClose();
  };

  return (
    <>
      <h4>Open tables:</h4>
      <RadioGroup
        aria-label="Digital bill overview"
        className="h-fit-content pt-4"
        value={activeDigitalBill?.reference || (!tableNumber ? 'None' : undefined)}
        onChange={handleChange}
      >
        <>
          <Radio key="none" className="py-3" value="None">
            <b>No table</b>
          </Radio>
          {[...digitalBills]
            .sort((a, b) =>
              a.primaryReference.localeCompare(b.primaryReference, undefined, { numeric: true, sensitivity: 'base' })
            )
            .map((digitalBill) => (
              <Radio key={digitalBill.reference} className="text-small py-3" value={digitalBill.reference}>
                <section className="flex justify-between w-full items-center">
                  <section className="flex flex-col flex-grow-1">
                    <h4>{`${intl.formatMessage({ id: 'checkout.deliveryPoint.TABLE' })} ${
                      digitalBill.primaryReference
                    }`}</h4>

                    {digitalBill.metadata?.tableName && (
                      <span className="text-sm">{digitalBill.metadata?.tableName}</span>
                    )}
                  </section>
                  <section className="flex flex-col gap-1">
                    <span className="text-grey-dark text-right">{formatCurrency(digitalBill.sum)}</span>
                    {digitalBill.metadata?.numberOfGuests && (
                      <section className="flex gap-1 ml-auto text-grey-dark">
                        <UsersmultipleIcon width={16} height={16} className="relative top-[2px]" />
                        <span>{digitalBill.metadata?.numberOfGuests}</span>
                      </section>
                    )}
                  </section>
                </section>
              </Radio>
            ))}
        </>
      </RadioGroup>
    </>
  );
};

export default SelectDigitalBill;
